import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  House, Buildings, Student, Receipt, Money, ChartLine, ArrowsClockwise,
  Users, Briefcase, IdentificationCard, CalendarBlank, ClipboardText,
  UserGear, Gear, SignOut, List, X, GraduationCap, CalendarDots,
  CaretDoubleLeft, CaretDoubleRight, Sun, Moon, Bell, Tag
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import api, { API } from "../lib/api";

const MENU = [
  { section: "Beranda", items: [
    { to: "/", icon: House, label: "Dashboard", roles: ["superadmin","admin","hr","finance","karyawan"] },
  ]},
  { section: "Keuangan Siswa", items: [
    { to: "/lembaga", icon: Buildings, label: "Lembaga", roles: ["superadmin","admin","finance"] },
    { to: "/siswa", icon: Student, label: "Siswa", roles: ["superadmin","admin","finance"] },
    { to: "/tagihan", icon: Receipt, label: "Tagihan", roles: ["superadmin","admin","finance"] },
    { to: "/rekap-tagihan", icon: ClipboardText, label: "Rekap Tagihan", roles: ["superadmin","admin","finance"] },
    { to: "/pembayaran", icon: Money, label: "Pembayaran", roles: ["superadmin","admin","finance"] },
    { to: "/approval-pembayaran", icon: ClipboardText, label: "Approval Pembayaran", roles: ["superadmin","admin","finance"] },
  ]},
  { section: "Keuangan Internal", items: [
    { to: "/nomenklatur", icon: Tag, label: "Nomenklatur", roles: ["superadmin","admin","finance"] },
    { to: "/pos-keuangan", icon: ChartLine, label: "Pos Keuangan", roles: ["superadmin","admin","finance"] },
    { to: "/kegiatan", icon: ClipboardText, label: "Proposal & LPJ", roles: ["superadmin","admin","hr","finance"] },
    { to: "/pengeluaran", icon: ArrowsClockwise, label: "Pengeluaran", roles: ["superadmin","admin","finance"] },
    { to: "/cashflow", icon: ChartLine, label: "Cashflow & Saldo", roles: ["superadmin","admin","finance"] },
    { to: "/laporan-keuangan", icon: ClipboardText, label: "Laporan Keuangan", roles: ["superadmin","admin","finance"] },
  ]},
  { section: "Human Resource", items: [
    { to: "/employees", icon: Users, label: "Karyawan", roles: ["superadmin","admin","hr"] },
    { to: "/slip-gaji", icon: Money, label: "Slip Gaji", roles: ["superadmin","admin","hr","karyawan"] },
    { to: "/cuti", icon: CalendarBlank, label: "Cuti & Izin", roles: ["superadmin","admin","hr","karyawan"] },
    { to: "/rapor", icon: IdentificationCard, label: "Rapor Guru", roles: ["superadmin","admin","hr","karyawan"] },
    { to: "/rapor-biah", icon: IdentificationCard, label: "Rapor Bi'ah Qur'an", roles: ["superadmin","admin","hr","karyawan"] },
    { to: "/rekrutmen", icon: Briefcase, label: "Rekrutmen", roles: ["superadmin","admin","hr"] },
  ]},
  { section: "Agenda", items: [
    { to: "/kalender", icon: CalendarDots, label: "Kalender", roles: ["superadmin","admin","hr","finance","karyawan"] },
    { to: "/agenda-manager-keuangan", icon: ClipboardText, label: "Agenda Mgr. Keuangan", roles: ["superadmin"] },
  ]},
  { section: "Sistem", items: [
    { to: "/users", icon: UserGear, label: "Pengguna", roles: ["superadmin","admin"] },
    { to: "/rekening", icon: Buildings, label: "Rekening Yayasan", roles: [] },
    { to: "/settings", icon: Gear, label: "Pengaturan", roles: ["superadmin","admin"] },
  ]},
];

const ROLE_LABELS = {
  superadmin: { title: "Super Admin Yayasan", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  admin: { title: "Administrator", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  hr: { title: "Human Resource", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  finance: { title: "Bagian Keuangan", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  karyawan: { title: "Karyawan / Guru", color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" },
};

// Bottom nav items (5 max, role-based)
const BOTTOM_NAV = {
  superadmin: [
    { to: "/", icon: House, label: "Home" },
    { to: "/pembayaran", icon: Money, label: "Pembayaran" },
    { to: "/employees", icon: Users, label: "Karyawan" },
    { to: "/cashflow", icon: ChartLine, label: "Cashflow" },
    { to: "/settings", icon: Gear, label: "Menu" },
  ],
  admin: [
    { to: "/", icon: House, label: "Home" },
    { to: "/pembayaran", icon: Money, label: "Pembayaran" },
    { to: "/employees", icon: Users, label: "Karyawan" },
    { to: "/cashflow", icon: ChartLine, label: "Cashflow" },
    { to: "/settings", icon: Gear, label: "Menu" },
  ],
  hr: [
    { to: "/", icon: House, label: "Home" },
    { to: "/employees", icon: Users, label: "Karyawan" },
    { to: "/slip-gaji", icon: Money, label: "Gaji" },
    { to: "/cuti", icon: CalendarBlank, label: "Cuti" },
    { to: "/rekrutmen", icon: Briefcase, label: "Rekrut" },
  ],
  finance: [
    { to: "/", icon: House, label: "Home" },
    { to: "/pembayaran", icon: Money, label: "Bayar" },
    { to: "/pengeluaran", icon: ArrowsClockwise, label: "Keluar" },
    { to: "/cashflow", icon: ChartLine, label: "Saldo" },
    { to: "/laporan-keuangan", icon: ClipboardText, label: "Laporan" },
  ],
  karyawan: [
    { to: "/", icon: House, label: "Home" },
    { to: "/slip-gaji", icon: Money, label: "Slip Gaji" },
    { to: "/cuti", icon: CalendarBlank, label: "Cuti" },
    { to: "/rapor", icon: IdentificationCard, label: "Rapor" },
    { to: "/kalender", icon: CalendarDots, label: "Agenda" },
  ],
};

export default function Layout() {
  const { user, settings, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("ef_sidebar_collapsed") === "1");
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { localStorage.setItem("ef_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (user?.role === "karyawan" && !profile) {
      (async () => {
        try {
          const { data } = await api.get("/employees/me");
          setProfile(data);
        } catch (_) {}
      })();
    }
  }, [user?.role, profile]);

  const scopedAdmin = user?.role === "admin" && !!user?.lembaga_id;
  const canSee = (roles, to) => {
    if (to === "/settings" && scopedAdmin) return false;
    return roles.includes(user?.role) || user?.role === "superadmin";
  };
  const logoUrl = settings?.logo_path ? `${API}/files/${settings.logo_path}` : null;
  const foundation = settings?.foundation_name || "Yayasan EduFoundation";
  const foundationShort = settings?.foundation_short_name || "EduFoundation";
  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.karyawan;
  const jabatan = user?.role === "karyawan" ? (profile?.position || "Karyawan") : roleInfo.title;
  const bottomNav = (BOTTOM_NAV[user?.role] || BOTTOM_NAV.karyawan).map((it) =>
    it.to === "/settings" && scopedAdmin ? { ...it, to: "/users", label: "Pengguna", icon: UserGear } : it
  );

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const asideWidth = collapsed ? "lg:w-20" : "lg:w-72";

  return (
    <div className="min-h-screen bg-background flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 ${asideWidth} bg-card border-r border-border flex flex-col transition-all duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="h-16 px-4 flex items-center gap-3 border-b border-border shrink-0">
          <div className="h-10 w-10 rounded-xl gradient-brand text-white flex items-center justify-center overflow-hidden shrink-0 shadow-md">
            {logoUrl ? <img src={logoUrl} alt="logo" className="h-full w-full object-cover" /> : <GraduationCap size={22} weight="duotone" />}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-foreground text-sm truncate">{foundationShort}</div>
              <div className="text-[10px] text-muted-foreground tracking-wider uppercase truncate">{foundation}</div>
            </div>
          )}
          <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(false)}><X size={20} /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
          {MENU.map((sec) => {
            const visible = sec.items.filter((i) => canSee(i.roles, i.to));
            if (visible.length === 0) return null;
            return (
              <div key={sec.section}>
                {!collapsed && <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{sec.section}</div>}
                <div className="space-y-0.5">
                  {visible.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.to === "/"}
                      data-testid={`nav-${it.to.replace("/","") || "dashboard"}`}
                      className={({ isActive }) =>
                        `sidebar-link flex items-center gap-3 mx-1 px-3 py-2.5 rounded-lg text-sm font-medium ${collapsed ? "justify-center" : ""} ${
                          isActive ? "bg-primary/10 text-secondary" : "text-foreground/70 hover:bg-muted hover:text-foreground"
                        }`
                      }
                      title={collapsed ? it.label : ""}
                    >
                      {({ isActive }) => (
                        <>
                          <it.icon size={19} weight={isActive ? "fill" : "duotone"} />
                          {!collapsed && <span className="truncate">{it.label}</span>}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="border-t border-border p-2 shrink-0">
          <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${collapsed ? "justify-center" : ""}`}>
            <div className="h-9 w-9 rounded-full gradient-brand text-white flex items-center justify-center font-bold shrink-0">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{jabatan}</div>
                </div>
                <button onClick={handleLogout} className="text-muted-foreground hover:text-destructive" data-testid="logout-button" title="Keluar">
                  <SignOut size={18} />
                </button>
              </>
            )}
          </div>
          {/* Collapse toggle - desktop only */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex mt-1 w-full items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-muted transition-colors"
            data-testid="sidebar-collapse"
          >
            {collapsed ? <CaretDoubleRight size={14} /> : <><CaretDoubleLeft size={14} /> <span>Ciutkan</span></>}
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 glass border-b border-border h-16 flex items-center px-4 lg:px-8 gap-3">
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(true)} data-testid="sidebar-toggle"><List size={22} /></button>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${roleInfo.color.split(" ").filter((c)=>c.includes("text-")).join(" ")}`}>{roleInfo.title}</div>
            <div className="font-display font-bold text-foreground truncate">Halo, {user?.name?.split(" ")[0]}</div>
          </div>
          <span className={`hidden sm:inline-block px-3 py-1 rounded-full text-[11px] font-semibold ${roleInfo.color}`}>{jabatan}</span>
          <button onClick={toggleTheme} className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title={theme === "dark" ? "Mode Terang" : "Mode Gelap"} data-testid="theme-toggle">
            {theme === "dark" ? <Sun size={16} weight="duotone" /> : <Moon size={16} weight="duotone" />}
          </button>
          <button className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" title="Notifikasi">
            <Bell size={16} weight="duotone" />
          </button>
        </header>

        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8 animate-fade-in">
          <Outlet />
        </main>

        {/* Bottom nav - mobile only */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bottom-nav bg-card border-t border-border flex items-center justify-around py-1.5 px-1">
          {bottomNav.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg min-w-[60px] transition-colors ${isActive ? "text-secondary" : "text-muted-foreground"}`}
              data-testid={`bnav-${it.to.replace("/","") || "dashboard"}`}
            >
              {({ isActive }) => (
                <>
                  <it.icon size={20} weight={isActive ? "fill" : "duotone"} />
                  <span className="text-[10px] font-semibold">{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
